import { React, useEffect, useState } from 'react'

// mui components
import {
  Container,
  Typography,
  Avatar,
  Grid,
  Button,
  Box,
  FormLabel
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding } from '@fortawesome/free-regular-svg-icons'

import GlobalInput from '../form/FreeFormInput'
import GlobalAddressInput from '../form/AddressInput'
import NumberInput from '../form/NumberInput'
import { companyProfileStyles } from '../../styles/classes/CompanySettingsClasses'
import { imageExtensions } from '../../lib/Constants'

export const CompanyProfileComponent = props => {
  const { classes } = companyProfileStyles()
  const { t } = useTranslation()
  const { profile, handleChange, handleImageChange, afterHoursPhone } = props
  const [address, setAddress] = useState({ ...profile?.address })

  useEffect(() => {
    handleChange(address, 'address')
  }, [address])

  const handleBusinessHours = (value, field) => {
    const newObj = { ...profile.business_hours }
    newObj[field] = value
    handleChange(newObj, 'business_hours')
  }

  const handleAfterHours = (value, field) => {
    const newObj = { ...profile.after_hours }
    newObj[field] = value
    handleChange(newObj, 'after_hours')
  }

  const addressChanged = (value, field, addressInfo) => {
    const newAddress = {
      address: value,
      address1: addressInfo?.address1,
      city: addressInfo?.city,
      country: addressInfo?.country,
      state: addressInfo?.state,
      zipCode: addressInfo?.zipCode
    }
    setAddress(newAddress)
  }
  const handleFocus = event => event.target.select()

  return (
    <Container
      maxWidth={false}
      data-testid="company_edit_container"
      classes={{ root: classes.infoContainer }}
    >
      <Grid
        container
      >
        <Grid item xs={12}>
          <Typography classes={{ root: classes.title }}>
            {t('company_profile.company_profile')}
          </Typography>
          <Typography classes={{ root: classes.subtitle }}>
            {t('company_profile.message.name_and_details')}
          </Typography>
        </Grid>
      </Grid>
      {props.showLogo
        ? (
        <Box display="flex" className={classes.headerContainer}>
          <Box flex={1} display="flex" alignItems="center">
            {props.logoError && (
              <Avatar alt="profile" className={classes.errorAvatar}>
                <FormLabel className={classes.error}>
                  {props.helperText}
                </FormLabel>
              </Avatar>
            )}
            {!props.logoError && (
              <Avatar
                alt="profile"
                src={profile?.tech_support?.customer_logo ?? null}
                className={
                  profile?.tech_support?.customer_logo
                    ? classes.avatar
                    : classes.emptyAvatar
                }
                imgProps={{ style: { objectFit: 'contain' } }}
              >
                <FontAwesomeIcon icon={faBuilding} size="4x" />
              </Avatar>
            )}
          </Box>
          <Box flex={4} display="flex" alignItems="center">
            <Button
              id="profile-logo"
              variant="contained"
              component="label"
              className={classes.uploadButton}
            >
              {t('company_profile.labels.upload')}
              <input
                hidden
                accept={imageExtensions.join(', ')}
                type="file"
                onChange={handleImageChange}
              />
            </Button>
          </Box>
        </Box>
          )
        : (
            null
          )}
      <Box>
        <Box display="flex">
          <Box marginRight="5px" flex={5} key="address-box">
            <GlobalInput
              onChange={handleChange}
              field="name"
              placeholder={t('company_profile.placeholder.company_legal')}
              value={profile?.name}
              label={t('company_profile.labels.company_legal')}
              required={
                props.requiredFields &&
                Object.prototype.hasOwnProperty.call(
                  props?.requiredFields,
                  'name'
                )
              }
            />
          </Box>
          <Box flex={1} />
        </Box>

        <Box display="flex">
          <Box marginRight="5px" flex={5} key="address-box">
            <GlobalAddressInput
              id="address"
              field="address"
              name="address"
              label={t('company_settings.info_card.address')}
              value={profile?.address?.address ?? ''}
              onChange={addressChanged}
              onFocus={handleFocus}
              required={
                props.requiredFields &&
                Object.prototype.hasOwnProperty.call(
                  props?.requiredFields,
                  'address'
                )
              }
            />
          </Box>
          <Box flex={1} />
        </Box>
        <Box display="flex">
          <Box flex={5}>
            <Box display="flex">
              <Box flex={4} key="business_hours_box">
                <NumberInput
                  key="business_hours_input"
                  onChange={handleBusinessHours}
                  field="phone"
                  placeholder={t('company_profile.placeholder.business_hours')}
                  value={profile?.business_hours?.phone}
                  label={t('company_profile.labels.business_hours')}
                  helperText={
                    props.profileError.businessPhoneError
                      ? t('company_profile.error.phone')
                      : null}
                  format="##########"
                  error={props.profileError.businessPhoneError}
                />
              </Box>
              <Box flex={0.5} />
              <Box flex={4} key="after_hours_box">
                <NumberInput
                  key="after_hours_input"
                  onChange={handleAfterHours}
                  field="phone"
                  placeholder={t('company_profile.placeholder.after_hours')}
                  value={afterHoursPhone}
                  label={t('company_profile.labels.after_hours')}
                  helperText={
                    props.profileError.afterHoursPhoneError
                      ? t('company_profile.error.phone')
                      : null
                  }
                  format="##########"
                  error={props.profileError.afterHoursPhoneError}
                />
              </Box>
            </Box>
          </Box>
          <Box flex={1} />
        </Box>
        <Box display="flex">
          <Box flex={5}>
            <Box display="flex">
              <Box flex={4} key="email_box">
                <GlobalInput
                  onChange={handleChange}
                  field="email"
                  placeholder={t('company_profile.placeholder.dispatch')}
                  value={profile?.email}
                  label={t('company_profile.labels.email')}
                  error={props.dispatchError}
                  helperText={t('company_profile.error.email')}
                  required={
                    props.requiredFields &&
                    Object.prototype.hasOwnProperty.call(
                      props?.requiredFields,
                      'email'
                    )
                  }
                />
              </Box>
              <Box flex={0.5} />
              <Box flex={4} key="website_box">
                <GlobalInput
                  onChange={handleChange}
                  field="website"
                  placeholder={t('company_profile.placeholder.company_website')}
                  value={profile?.website}
                  label={t('company_profile.labels.company_website')}
                  error={props.invoiceError}
                  helperText={t('company_profile.error.company_website')}
                  required={
                    props.requiredFields &&
                    Object.prototype.hasOwnProperty.call(
                      props?.requiredFields,
                      'website'
                    )
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box flex={1} />
        </Box>
      </Box>
    </Container>
  )
}
